import React, { useState, useCallback, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useOrdersPolling } from '../hooks/useOrdersPolling';
import { ordersApi } from '../api';
import { Button } from '../components/UI/Button';
import {
  OrderForm,
  AccountSummaryCard,
  PositionsCard,
  OrdersTable,
  OrderFilters,
  OrderDetailModal,
  CancelOrderConfirmation,
  AmendOrderModal,
} from '../components/Orders';
import type { OrderFilterState } from '../components/Orders';
import type { OrderDetailResponse, OrderReplaceRequest } from '../types/api';

const ORDERS_PER_PAGE = 25;

export function Orders() {
  const { currentAccount, hasPermission } = useAuth();
  const { showToast } = useToast();
  const canSubmitOrders = hasPermission('order_placement');

  // UI State
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetailResponse | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showAmendModal, setShowAmendModal] = useState(false);

  // Action loading states
  const [isCancelling, setIsCancelling] = useState(false);
  const [isAmending, setIsAmending] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<OrderFilterState>({
    status: 'all',
    symbol: '',
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: ORDERS_PER_PAGE,
  });

  // Use polling hook for data
  const {
    orders,
    account,
    ordersLoading,
    accountLoading,
    ordersError,
    accountError,
    total,
    refreshOrders,
    refreshAll,
    pausePolling,
    resumePolling,
  } = useOrdersPolling({
    accountId: currentAccount?.account_id || '',
    filters,
    pagination,
  });

  // Pause polling when modals are open
  useEffect(() => {
    if (showDetailModal || showCancelConfirm || showAmendModal || showOrderForm) {
      pausePolling();
    } else {
      resumePolling();
    }
  }, [showDetailModal, showCancelConfirm, showAmendModal, showOrderForm, pausePolling, resumePolling]);

  // Handle order form success
  const handleOrderSuccess = useCallback(() => {
    setShowOrderForm(false);
    refreshAll();
  }, [refreshAll]);

  // Handle order form cancel
  const handleOrderCancel = useCallback(() => {
    setShowOrderForm(false);
  }, []);

  // Handle view details
  const handleViewDetails = useCallback((order: OrderDetailResponse) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  }, []);

  // Handle cancel order click
  const handleCancelOrderClick = useCallback((order: OrderDetailResponse) => {
    setSelectedOrder(order);
    setShowCancelConfirm(true);
  }, []);

  // Handle amend order click
  const handleAmendOrderClick = useCallback((order: OrderDetailResponse) => {
    setSelectedOrder(order);
    setShowAmendModal(true);
  }, []);

  // Handle cancel order confirmation
  const handleCancelOrderConfirm = useCallback(async () => {
    if (!selectedOrder) return;

    setIsCancelling(true);
    try {
      await ordersApi.cancelOrder(selectedOrder.id);
      showToast(`Order ${selectedOrder.client_order_id} cancellation requested`, 'success');
      setShowCancelConfirm(false);
      setShowDetailModal(false);
      setSelectedOrder(null);
      refreshAll();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel order';
      showToast(message, 'error');
    } finally {
      setIsCancelling(false);
    }
  }, [selectedOrder, showToast, refreshAll]);

  // Handle amend order submission
  const handleAmendOrderSubmit = useCallback(
    async (changes: OrderReplaceRequest) => {
      if (!selectedOrder) return;

      setIsAmending(true);
      try {
        await ordersApi.replaceOrder(selectedOrder.id, changes);
        showToast(`Order ${selectedOrder.client_order_id} amendment requested`, 'success');
        setShowAmendModal(false);
        setShowDetailModal(false);
        setSelectedOrder(null);
        refreshAll();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to amend order';
        showToast(message, 'error');
      } finally {
        setIsAmending(false);
      }
    },
    [selectedOrder, showToast, refreshAll]
  );

  // Handle filter change
  const handleFilterChange = useCallback((newFilters: OrderFilterState) => {
    setFilters(newFilters);
    // Reset pagination when filters change
    setPagination((prev) => ({ ...prev, offset: 0 }));
  }, []);

  // Handle page change
  const handlePageChange = useCallback((offset: number) => {
    setPagination((prev) => ({ ...prev, offset }));
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Skip if inside an input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // N - New Order
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey && canSubmitOrders) {
        e.preventDefault();
        setShowOrderForm(true);
      }

      // R - Refresh
      if (e.key === 'r' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        refreshAll();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [canSubmitOrders, refreshAll]);

  return (
    <Layout>
      <div className="container-custom py-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
            {canSubmitOrders && !showOrderForm && (
              <Button variant="primary" onClick={() => setShowOrderForm(true)}>
                + New Order
              </Button>
            )}
          </div>

          {/* Order Form - Collapsible */}
          {showOrderForm && (
            <div className="mb-6">
              <OrderForm onSuccess={handleOrderSuccess} onCancel={handleOrderCancel} />
            </div>
          )}

          {/* Account Summary and Positions Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Account Summary Card */}
            <AccountSummaryCard
              accountId={currentAccount?.account_id || ''}
              buyingPower={account?.buying_power || '0'}
              cash={account?.cash || '0'}
              isLoading={accountLoading}
            />

            {/* Positions Card */}
            <PositionsCard
              positions={account?.positions || []}
              isLoading={accountLoading}
            />
          </div>

          {/* Account Error */}
          {accountError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {accountError}
            </div>
          )}

          {/* Orders Section */}
          <div className="space-y-4">
            {/* Filters */}
            <OrderFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onRefresh={refreshOrders}
              isRefreshing={ordersLoading}
            />

            {/* Orders Error */}
            {ordersError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {ordersError}
              </div>
            )}

            {/* Orders Table */}
            <OrdersTable
              orders={orders}
              isLoading={ordersLoading}
              onViewDetails={handleViewDetails}
              onCancelOrder={handleCancelOrderClick}
              onAmendOrder={handleAmendOrderClick}
              pagination={{
                total,
                offset: pagination.offset,
                limit: pagination.limit,
                onPageChange: handlePageChange,
              }}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedOrder && (
        <>
          <OrderDetailModal
            order={selectedOrder}
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedOrder(null);
            }}
            onCancel={() => {
              setShowDetailModal(false);
              setShowCancelConfirm(true);
            }}
            onAmend={() => {
              setShowDetailModal(false);
              setShowAmendModal(true);
            }}
          />

          <CancelOrderConfirmation
            order={selectedOrder}
            isOpen={showCancelConfirm}
            onClose={() => {
              setShowCancelConfirm(false);
              if (!showDetailModal) {
                setSelectedOrder(null);
              }
            }}
            onConfirm={handleCancelOrderConfirm}
            isLoading={isCancelling}
          />

          <AmendOrderModal
            order={selectedOrder}
            isOpen={showAmendModal}
            onClose={() => {
              setShowAmendModal(false);
              if (!showDetailModal) {
                setSelectedOrder(null);
              }
            }}
            onSubmit={handleAmendOrderSubmit}
            isLoading={isAmending}
          />
        </>
      )}
    </Layout>
  );
}
